/**
 * useDocumentParser Hook
 * 
 * Provides document parsing functionality for uploading and parsing
 * PDF, Excel, and other document formats. Uses the parse-document
 * edge function which supports multiple providers (Lovable AI, Azure).
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ParsedRow {
  rowIndex: number;
  data: Record<string, string>;
  confidence: number;
  page?: number;
}

export interface ParsedDocument {
  rows: ParsedRow[];
  headers: string[];
  overallConfidence: number;
  metadata: {
    provider: 'lovable' | 'azure';
    processingTimeMs: number;
    pageCount: number;
    ocrUsed: boolean;
    modelVersion: string;
    totalRequestTimeMs?: number;
  };
  warnings: Array<{
    severity: 'low' | 'medium' | 'high';
    message: string;
    affectedRows?: number[];
    suggestion?: string;
  }>;
  rawText?: string;
  documentType?: 'bank_statement' | 'tax_account' | 'invoice' | 'unknown';
}

export interface UseDocumentParserOptions {
  moduleType?: string;
  expectedHeaders?: string[];
  provider?: 'lovable' | 'azure';
  onSuccess?: (result: ParsedDocument) => void;
  onError?: (error: string) => void;
}

export function useDocumentParser(options: UseDocumentParserOptions = {}) {
  const { toast } = useToast();
  const [isParsing, setIsParsing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ParsedDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Convert a file to base64
   */
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Check if a file is a PDF
   */
  const isPdfFile = useCallback((file: File): boolean => {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  }, []);

  /**
   * Check if a file requires parsing (PDF, Excel)
   */
  const requiresParsing = useCallback((file: File): boolean => {
    const parsingMimeTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    return parsingMimeTypes.includes(file.type) || 
           file.name.toLowerCase().endsWith('.pdf') ||
           file.name.toLowerCase().endsWith('.xlsx') ||
           file.name.toLowerCase().endsWith('.xls');
  }, []);

  /**
   * Parse a document using the edge function
   */
  const parseDocument = useCallback(async (
    file: File,
    moduleType?: string
  ): Promise<ParsedDocument | null> => {
    setIsParsing(true);
    setProgress(10);
    setError(null);
    setResult(null);

    try {
      // Convert file to base64
      setProgress(20);
      const base64Content = await fileToBase64(file);
      
      setProgress(40);
      
      // Call edge function
      const { data, error: fnError } = await supabase.functions.invoke('parse-document', {
        body: {
          fileContent: base64Content,
          mimeType: file.type || 'application/pdf',
          moduleType: moduleType || options.moduleType,
          expectedHeaders: options.expectedHeaders,
          provider: options.provider,
          includeRawText: false,
        },
      });

      setProgress(80);

      if (fnError) {
        throw new Error(fnError.message || 'Parse function failed');
      }

      if (data.error) {
        throw new Error(data.details || data.error);
      }

      const parsedResult = data as ParsedDocument;
      setResult(parsedResult);
      setProgress(100);

      // Show warnings as toasts (but not success - let caller handle that)
      if (parsedResult.warnings?.length > 0) {
        const highWarnings = parsedResult.warnings.filter(w => w.severity === 'high');
        if (highWarnings.length > 0) {
          toast({
            title: 'Varning vid dokumentparsning',
            description: highWarnings[0].message,
            variant: 'destructive',
          });
        }
      }

      // Note: Success toast removed - caller (ModuleFilesTab) shows its own toast

      options.onSuccess?.(parsedResult);
      return parsedResult;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      
      toast({
        title: 'Dokumentparsning misslyckades',
        description: errorMessage,
        variant: 'destructive',
      });

      options.onError?.(errorMessage);
      return null;

    } finally {
      setIsParsing(false);
    }
  }, [fileToBase64, options, toast]);

  /**
   * Convert parsed document to CSV content for field mapping
   */
  const toCsvContent = useCallback((parsed: ParsedDocument): string => {
    if (!parsed || parsed.rows.length === 0) return '';

    const headers = parsed.headers;
    const csvLines: string[] = [];

    // Header row
    csvLines.push(headers.map(h => `"${h.replace(/"/g, '""')}"`).join(';'));

    // Data rows
    for (const row of parsed.rows) {
      const values = headers.map(h => {
        const val = row.data[h] || '';
        return `"${val.replace(/"/g, '""')}"`;
      });
      csvLines.push(values.join(';'));
    }

    return csvLines.join('\n');
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setIsParsing(false);
    setProgress(0);
    setResult(null);
    setError(null);
  }, []);

  return {
    parseDocument,
    isParsing,
    progress,
    result,
    error,
    reset,
    isPdfFile,
    requiresParsing,
    toCsvContent,
  };
}
